/**
 * @file coupons.store.ts
 *
 * Zustand store for the Coupons tab. Pairs an Orders-style server-paginated,
 * searchable list (`coupons` / totals / `fetchCoupons` + `deleteCoupon`) with the
 * shared {@link createFormSlice} for the routed create/edit form lifecycle
 * (`current` / `loadOne` / `submitCreate` / `submitUpdate` / `resetForm`), wired
 * to the coupons API with a cache-first lookup over the loaded page. The slice's
 * `formErrorCode` lets the form map a `duplicate_code` / `invalid_amount` failure
 * to a specific field; other errors are coerced with {@link toMessage}.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  fetchCoupons as apiFetchCoupons,
  fetchCouponById as apiFetchCouponById,
  createCoupon as apiCreateCoupon,
  updateCoupon as apiUpdateCoupon,
  deleteCoupon as apiDeleteCoupon,
} from '../api/coupons';
import type {
  FetchCouponsParams,
  ShopCoupon,
  ShopCouponPayload,
} from '../api/shop-types';
import { createFormSlice, toMessage, type FormSlice } from './store-utils';

/**
 * Shape of the coupons store: the current page of coupons plus the shared
 * {@link FormSlice} that seeds and persists the create/edit form.
 */
interface CouponsState extends FormSlice<ShopCoupon, ShopCouponPayload, string> {
  /** The current page of coupons. */
  coupons: ShopCoupon[];
  /** Total number of coupons across all pages. */
  couponsTotal: number;
  /** Total number of coupon pages. */
  couponsTotalPages: number;
  /** Whether a list fetch is in flight. */
  loading: boolean;
  /** Last list/delete error message, or `null` on success. */
  error: string | null;

  /**
   * Fetches a page of coupons, replacing the current list.
   *
   * @param params - Optional pagination and search filters.
   */
  fetchCoupons: (params?: FetchCouponsParams) => Promise<void>;

  /**
   * Permanently deletes a coupon.
   *
   * @param id - The coupon id to delete.
   *
   * @returns `true` on success, `false` when the request fails.
   */
  deleteCoupon: (id: string) => Promise<boolean>;
}

export const useCouponsStore = create<CouponsState>()(
  devtools(
    (set, get) => ({
      coupons: [],
      couponsTotal: 0,
      couponsTotalPages: 0,
      loading: false,
      error: null,

      ...createFormSlice<CouponsState, ShopCoupon, ShopCouponPayload, ShopCoupon, string>({
        name: 'coupons',
        fetchById: apiFetchCouponById,
        create: apiCreateCoupon,
        update: apiUpdateCoupon,
        selectCached: (get) => (id) => get().coupons.find((c) => c.id === id),
      })(set, get),

      fetchCoupons: async (params = {}) => {
        set({ loading: true, error: null }, false, 'coupons/fetch/pending');
        try {
          const res = await apiFetchCoupons(params);
          set(
            {
              coupons: res.data,
              couponsTotal: res.total,
              couponsTotalPages: res.totalPages,
              loading: false,
            },
            false,
            'coupons/fetch/fulfilled',
          );
        } catch (err) {
          set({ loading: false, error: toMessage(err) }, false, 'coupons/fetch/rejected');
        }
      },

      deleteCoupon: async (id) => {
        try {
          await apiDeleteCoupon(id);
          return true;
        } catch (err) {
          set({ error: toMessage(err) }, false, 'coupons/delete/rejected');
          return false;
        }
      },
    }),
    { name: 'shop/coupons' },
  ),
);
